'use client';

import styled, { css } from 'styled-components';
import { useEffect, useRef } from 'react';
import Image from 'next/legacy/image';
import { Button, Placeholder, Text } from '@lmn/design-system';
import { IconArrowLeftLinear, IconArrowRightLinear } from '@/app/common/icons/libraryIcons';
import { useTranslation } from '@/app/components/translations/Translation';
import { useAlternativeDates } from '@/app/components/hotel-detail/hooks/useAlternativeDates';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import ScrollableHorizontal from '@/app/components/hotel-detail/ScrollableHorizontal';
import {
  trackUnavailableRoomsChangeDates,
  trackUnavailableRoomsSeeOtherHotels,
} from '@/app/components/hotel-detail/trackInteractionEvents';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import { getNoResultsImage } from '../search/components/NoResults/utils';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const DatesCarouselContainer = styled.div`
  position: relative;
  width: 100%;
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  flex-align: center;
  justify-content: center;
  width: 100%;
`;

const DatesCarousel = styled(ScrollableHorizontal)`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
`;

const AlternativeDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 8px 16px 12px 16px;
  min-width: fit-content;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    border: 1px solid ${(p) => p.theme.commons.colors.primary400};
  }
`;

const Separator = styled.div`
  min-height: 1px;
  background-color: ${(p) => p.theme.commons.colors.gray100};
  min-width: 106px;
  width: 100%;
`;

const Layer = styled.div`
  position: absolute;
  height: 100%;
  width: 40px;
  display: flex;
  align-items: center;
  top: 0;
`;

const LeftLayer = styled(Layer)`
  left: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 1) 25%, rgba(255, 255, 255, 0) 100%);
  margin-left: -2px;
  justify-content: flex-start;
`;

const RightLayer = styled(Layer)`
  right: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 25%, rgba(255, 255, 255, 1) 100%);
  margin-right: -2px;
  justify-content: flex-end;
`;

const PaginationButton = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  border: 0;
  background: white;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};

  &:hover {
    box-shadow: ${({ theme }) => theme.kallisto.shadow.smallDownShadow};
  }

  & > [data-library-icon] {
    width: 16px;
    height: 16px;
  }
`;

export default function SoldOutAlternativeDates() {
  const { t } = useTranslation();
  const { data, isLoading } = useAlternativeDates();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { handleBackToResults } = useCommonClient();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  const { isPastCheckin, checkin, checkout } = useUrlSearchParams();

  // The price calendar behind this carousel can still price the stay the page just found sold out.
  // Offering it back is a dead end: re-picking the current dates changes no pricing/rooms key, so
  // nothing refetches and the page would load forever after resetOffers(). (MPH-1140)
  const products = data?.products?.filter((product) => product.from !== checkin || product.to !== checkout);
  const showAlternativeDates = (products?.length ?? 0) >= 3;

  const onChangeDatesClick = () => {
    trackUnavailableRoomsChangeDates();
    sendCuriosityClickEvent({
      event_name: 'click_error_no_alternative_dates_change_dates',
    });
    window.dispatchEvent(new Event('csw:toggleCollapsedSearch'));
  };

  const onSeeOtherHotelsClick = () => {
    trackUnavailableRoomsSeeOtherHotels();
    sendCuriosityClickEvent({
      event_name: 'click_error_no_alternative_dates_change_hotel',
    });
    handleBackToResults({ highlightHotel: false });
  };

  useEffect(() => {
    if (products?.length !== undefined) {
      if (showAlternativeDates) {
        sendCuriosityClickEvent(
          {
            event_name: 'error_carrousel_shown',
            event_context: products.length.toString(),
          },
          { onlyOnce: true }
        );
      } else {
        sendCuriosityClickEvent(
          {
            event_name: 'error_no_alternative_dates_shown',
          },
          { onlyOnce: true }
        );
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products?.length]);

  return (
    <Container>
      {isPastCheckin ? (
        // This image is obtained from CDN and fails when using the Image component
        <img
          className="openx-ui-filters-no-results-img"
          src={getNoResultsImage({
            localization,
            hasFilters: false,
            searchIsInThePast: true,
            shouldResetMap: false,
          })}
          alt={`Dates in the past`}
          width={180}
          height={150}
        />
      ) : (
        <Image src={`${imageBasePath}soldout-alternative-dates.svg`} alt={`Sold out`} width="180" height="150" />
      )}

      <Text
        variant="Title3"
        css={`
          text-align: center;
        `}
      >
        {isPastCheckin
          ? t('vacation.oss.noresults.title_expired')
          : t('vacation.select.rooms.soldout.alternative_dates.title')}
      </Text>
      <Text
        variant="BodySmall"
        css={css`
          text-align: center;
        `}
      >
        {t(
          showAlternativeDates
            ? 'vacation.select.rooms.soldout.alternative_dates.subtitle'
            : 'vacation.select.soldout.text'
        )}
      </Text>
      {isLoading ? (
        <div
          css={`
            display: flex;
            gap: 24px;
          `}
        >
          <Placeholder width="160px" height="40px" />
          <Placeholder width="160px" height="40px" />
        </div>
      ) : showAlternativeDates && products ? (
        <AlternativeDatesCarousel products={products} />
      ) : (
        <CTAButtonsContainer>
          <Button
            onClick={onSeeOtherHotelsClick}
            variant="primary"
            css={css`
              margin-right: 1rem;
            `}
            data-testid="see-other-hotels"
          >
            {t('vacation.select.soldout.cta.other.hotels')}
          </Button>
          <Button
            onClick={onChangeDatesClick}
            variant="primary"
            css={css`
              margin-left: 1rem;
            `}
            data-testid="change-dates"
          >
            {t('vacation.select.soldout.cta.change.dates')}
          </Button>
        </CTAButtonsContainer>
      )}
    </Container>
  );
}

function AlternativeDatesCarousel(props: { products: AlternativeDate[] }) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { setDates } = useUrlSearchParams();
  const { resetOffers } = useOffersContext();
  const { sendCuriosityClickEvent } = useCuriosity();

  const ref = useRef(null);
  const refPaginationLeft = useRef<HTMLDivElement>(null);
  const refPaginationRight = useRef<HTMLDivElement>(null);

  const onAlternativeDateClick = (product: AlternativeDate) => {
    resetOffers();
    setDates({
      checkin: product.from,
      checkout: product.to,
      nights: product.nights,
      origin: 'ALTERNATIVE_DATES',
    });
    sendCuriosityClickEvent({
      event_name: 'click_carrousel',
    });
  };

  const onLeftPaginationClick = () => {
    paginate('left');
  };

  const onRightPaginationClick = () => {
    paginate('right');
  };

  const paginate = (direction: 'left' | 'right') => {
    const container = ref.current as HTMLElement | null;
    if (!container) return;
    const newPosition =
      direction === 'left'
        ? container.scrollLeft - container.offsetWidth
        : container.scrollLeft + container.offsetWidth;
    container?.scrollTo({ left: newPosition, behavior: 'smooth' });
  };

  useEffect(() => {
    const container = ref.current as HTMLElement | null;
    if (!container) return;

    function onScroll() {
      if (!container || !refPaginationLeft.current || !refPaginationRight.current) return;
      const noScroll = container.offsetWidth >= container.scrollWidth;

      if (noScroll || container.scrollLeft === 0) {
        refPaginationLeft.current.style.display = 'none';
      } else {
        refPaginationLeft.current.style.display = 'flex';
      }

      if (noScroll || Math.ceil(container.scrollLeft) + container.offsetWidth >= container.scrollWidth) {
        refPaginationRight.current.style.display = 'none';
      } else {
        refPaginationRight.current.style.display = 'flex';
      }
    }
    container.addEventListener('scroll', onScroll, true);
    onScroll();

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', onScroll, true);
    };
  }, [refPaginationLeft, refPaginationRight]);

  return (
    <DatesCarouselContainer>
      <LeftLayer ref={refPaginationLeft}>
        <PaginationButton onClick={() => onLeftPaginationClick()} onKeyUp={keyUpHandler(onLeftPaginationClick)}>
          <IconArrowLeftLinear />
        </PaginationButton>
      </LeftLayer>
      <RightLayer ref={refPaginationRight}>
        <PaginationButton onClick={() => onRightPaginationClick()} onKeyUp={keyUpHandler(onRightPaginationClick)}>
          <IconArrowRightLinear />
        </PaginationButton>
      </RightLayer>
      <DatesCarousel containerRef={ref}>
        {props.products.map((product) => (
          <AlternativeDateContainer
            key={`${product.from}-${product.price}`}
            onClick={() => onAlternativeDateClick(product)}
            onKeyUp={keyUpHandler(() => onAlternativeDateClick(product))}
            role="button"
            aria-label={`select alternative date - from ${product.dates.from} to ${product.dates.to}`}
            tabIndex={0}
          >
            <Text variant="Title6">
              {product.dates.from} - {product.dates.to}
            </Text>
            <Text
              variant="Caption"
              css={`
                padding-bottom: 4px;
              `}
            >
              {product.nights > 1
                ? `${product.nights} ${t('vacation.common.nights')}`
                : `${product.nights} ${t('vacation.common.night')}`}
            </Text>
            <Separator />
            <Text
              variant="Title6"
              css={css`
                color: ${(p) => p.theme.commons.colors.primary400};
              `}
            >{`${t('vacation.common.price.from')} ${formatPrice({ price: product.price, localization })}`}</Text>
            <Text
              variant="Caption"
              css={css`
                color: ${(p) => p.theme.commons.colors.primary400};
              `}
            >
              {(product.priceType === 'PER_PERSON'
                ? t('vacation.common.price.per_person')
                : t('vacation.common.price.total')
              ).toLowerCase()}
            </Text>
          </AlternativeDateContainer>
        ))}
      </DatesCarousel>
    </DatesCarouselContainer>
  );
}
